/**
 * Label line builder for ramp aircraft sticky labels
 *
 * Takes enriched allocation data and the list of enabled fields/status groups,
 * returns compact display values with tooltip descriptions.
 *
 * Warnings are now sourced from computeStatusRows() (same data as the aircraft card)
 * instead of the old compute-ramp-warnings system.
 */

import type { AllocationEnrichedData, StatusRowSeverity } from '@/types/aircraft-card'
import type { RampLabelField } from '@/stores/rampViewConfigStore'
import type { WarningDisplayMode } from '@/stores/rampViewConfigStore'
import { RAMP_LABEL_FIELDS } from '@/stores/rampViewConfigStore'
import { formatCurrency, formatDate, formatRelativeDate, formatClientName } from './format-business-data'
import { POSITION_TYPE_LABELS } from '@/types/allocations'
import { LEASE_STATUS_LABELS, LEASE_TYPE_LABELS } from '@/types/leases'
import { TRANSIENT_STATUS_LABELS } from '@/types/transient-stays'
import { computeStatusRows, type StatusRowGroup, type StatusRowDef } from './compute-status-rows'

export interface LabelLine {
  value: string
  tooltip: string
  /** 'data' for standard fields, 'warning' for full-text warning badges,
   *  'compact-warning' for icon-only compact warning badges,
   *  'presence' for ghost state badges */
  type: 'data' | 'warning' | 'compact-warning' | 'presence'
  /** Severity level for warning/compact-warning lines (controls badge/circle color) */
  severity?: StatusRowSeverity
  /** For fuel progress: 0-100 percentage for mini progress bar */
  progress?: { percent: number; atRisk: boolean }
  /** Ghost presence mode for presence-type lines */
  presenceMode?: 'away' | 'incoming' | 'returning'
  /** Lucide icon name for compact-warning rendering */
  iconName?: string
  /** Extra tooltip lines (joined below the main tooltip text) */
  tooltipLines?: string[]
}

/**
 * Resolve a single field to its display value, or null if unavailable
 */
function resolveField(
  field: RampLabelField,
  data: AllocationEnrichedData
): string | null {
  switch (field) {
    case 'tailNumber':
      return data.aircraft.tailNumber || null

    case 'typeCode':
      return data.aircraft.typeCode || null

    case 'ownerName': {
      if (data.aircraft.owner) return formatClientName(data.aircraft.owner)
      if (data.aircraft.ownerName) return data.aircraft.ownerName
      // Fall back to lease/transient client name
      if (data.leaseContract?.clientName) return data.leaseContract.clientName
      if (data.transientStay?.clientName) return data.transientStay.clientName
      return null
    }

    case 'ownerContact': {
      const owner = data.aircraft.owner
      if (!owner) return null
      return owner.emailPrimary || owner.phonePrimary || null
    }

    case 'contractPrice': {
      if (data.leaseContract?.monthlyRate) {
        return formatCurrency(data.leaseContract.monthlyRate) + '/mo'
      }
      if (data.transientStay?.rateAmount) {
        return formatCurrency(data.transientStay.rateAmount) + '/nt'
      }
      return null
    }

    case 'contractStart': {
      const date = data.leaseContract?.startDate || data.transientStay?.checkIn
      return date ? formatDate(date) : null
    }

    case 'contractEnd': {
      const date = data.leaseContract?.endDate || data.transientStay?.expectedCheckOut
      return date ? formatDate(date) : null
    }

    case 'areaUnit': {
      const parts = [data.allocation.areaName, data.allocation.unitCode].filter(Boolean)
      return parts.length > 0 ? parts.join(' / ') : null
    }

    case 'positionType':
      return data.allocation.positionType
        ? POSITION_TYPE_LABELS[data.allocation.positionType] || data.allocation.positionType
        : null

    case 'insuranceExpiry':
      return data.aircraft.insuranceExpiry ? formatDate(data.aircraft.insuranceExpiry) : null

    case 'contractType': {
      if (data.leaseContract) {
        return LEASE_TYPE_LABELS[data.leaseContract.type] || 'Lease'
      }
      if (data.transientStay) return 'Transient'
      return null
    }

    case 'contractStatus': {
      if (data.leaseContract?.status) {
        return LEASE_STATUS_LABELS[data.leaseContract.status] || data.leaseContract.status
      }
      if (data.transientStay?.status) {
        return TRANSIENT_STATUS_LABELS[data.transientStay.status] || data.transientStay.status
      }
      return null
    }

    case 'contractNumber':
      return data.leaseContract?.contractNumber || data.transientStay?.bookingNumber || null

    case 'fuelProgress': {
      if (!data.fuelSummary) return null
      const { deliveredGallonsMonthly, commitmentGallonsMonthly, percentComplete } = data.fuelSummary
      if (commitmentGallonsMonthly != null && percentComplete != null) {
        return `${percentComplete}% (${deliveredGallonsMonthly}/${commitmentGallonsMonthly} gal)`
      }
      return `${deliveredGallonsMonthly} gal this month`
    }

    default:
      return null
  }
}

/**
 * Build data label lines for an aircraft based on enabled fields.
 * Skips fields that resolve to null (no empty lines in the label).
 */
export function buildLabelLines(
  data: AllocationEnrichedData,
  enabledFields: RampLabelField[]
): LabelLine[] {
  const lines: LabelLine[] = []

  for (const field of enabledFields) {
    const value = resolveField(field, data)
    if (value) {
      const config = RAMP_LABEL_FIELDS.find((f) => f.field === field)
      const line: LabelLine = {
        value,
        tooltip: config?.tooltip || field,
        type: 'data',
      }
      // Attach progress data for fuel progress label (only when commitment exists)
      if (field === 'fuelProgress' && data.fuelSummary && data.fuelSummary.percentComplete != null) {
        line.progress = {
          percent: data.fuelSummary.percentComplete,
          atRisk: data.fuelSummary.isAtRisk,
        }
      }
      lines.push(line)
    }
  }

  return lines
}

/**
 * Build warning label lines from status rows (replaces old computeWarnings approach).
 *
 * Filters out 'ok' severity rows (not actionable on map), rows outside enabledStatusGroups,
 * and individually disabled rows via disabledStatusRowKeys blocklist.
 * In 'full' mode, produces `type: 'warning'` lines with text badges.
 * In 'compact' mode, produces `type: 'compact-warning'` lines for icon-circle rendering.
 */
export function buildStatusRowLines(
  data: AllocationEnrichedData,
  enabledStatusGroups: StatusRowGroup[],
  disabledStatusRowKeys: string[],
  displayMode: WarningDisplayMode
): LabelLine[] {
  if (displayMode === 'off' || enabledStatusGroups.length === 0) return []

  const statusRows = computeStatusRows(data)

  // Filter out 'ok' rows, rows not in enabled groups, and individually disabled rows
  const filteredRows = statusRows.filter(
    (row) =>
      row.severity !== 'ok' &&
      enabledStatusGroups.includes(row.group) &&
      !disabledStatusRowKeys.includes(row.key)
  )

  if (filteredRows.length === 0) return []

  if (displayMode === 'compact') {
    return filteredRows.map((row) => buildCompactWarningLine(row))
  }

  // Full mode
  return filteredRows.map((row) => buildFullWarningLine(row))
}

function buildFullWarningLine(row: StatusRowDef): LabelLine {
  return {
    value: row.text,
    tooltip: row.tooltipLines ? row.tooltipLines.join('\n') : row.text,
    type: 'warning',
    severity: row.severity,
    iconName: row.iconName,
    tooltipLines: row.tooltipLines,
  }
}

function buildCompactWarningLine(row: StatusRowDef): LabelLine {
  return {
    value: row.text,
    tooltip: row.text,
    type: 'compact-warning',
    severity: row.severity,
    iconName: row.iconName,
    tooltipLines: row.tooltipLines,
  }
}

/**
 * Build presence-state badge lines for ghost aircraft.
 * Returns a presence pill showing the aircraft's operational state on the map.
 */
function buildPresenceLines(data: AllocationEnrichedData): LabelLine[] {
  const renderMode = data.allocation.renderMode
  if (!renderMode || renderMode === 'solid') return []

  if (renderMode === 'ghost_away') {
    const returnInfo = data.allocation.expectedReturnAt
      ? `Returns ${formatRelativeDate(data.allocation.expectedReturnAt)}`
      : null
    return [{
      value: returnInfo ? `Away · ${returnInfo}` : 'Away',
      tooltip: returnInfo
        ? `Aircraft is away, expected return: ${formatDate(data.allocation.expectedReturnAt!)}`
        : 'Aircraft is currently away',
      type: 'presence',
      presenceMode: 'away',
    }]
  }

  if (renderMode === 'ghost_incoming') {
    const arrivalDate = data.allocation.startsAt
    return [{
      value: arrivalDate ? `Arriving ${formatRelativeDate(arrivalDate)}` : 'Expected',
      tooltip: arrivalDate
        ? `Expected arrival: ${formatDate(arrivalDate)}`
        : 'Aircraft is expected',
      type: 'presence',
      presenceMode: 'incoming',
    }]
  }

  if (renderMode === 'ghost_returning') {
    const returnDate = data.allocation.expectedReturnAt
    return [{
      value: returnDate ? `Inbound · ${formatRelativeDate(returnDate)}` : 'Inbound',
      tooltip: returnDate
        ? `Aircraft is returning: ${formatDate(returnDate)}`
        : 'Aircraft is returning',
      type: 'presence',
      presenceMode: 'returning',
    }]
  }

  return []
}

/**
 * Build all label lines (presence first, warnings, then data) for an aircraft.
 * Presence badges show ghost state, warnings appear next, then data labels.
 */
export function buildAllLabelLines(
  data: AllocationEnrichedData,
  enabledFields: RampLabelField[],
  enabledStatusGroups: StatusRowGroup[],
  disabledStatusRowKeys: string[],
  warningDisplayMode: WarningDisplayMode
): LabelLine[] {
  const presenceLines = buildPresenceLines(data)
  const dataLines = buildLabelLines(data, enabledFields)
  const warningLines = buildStatusRowLines(data, enabledStatusGroups, disabledStatusRowKeys, warningDisplayMode)

  // Presence first (ghost state), then warnings (higher priority), then data labels
  return [...presenceLines, ...warningLines, ...dataLines]
}
